import {
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { WithGrow } from "@common/components/WithGrow";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import ParticipantCard from "./ParticipantCard";
import Typography from "@common/components/Typography";
import { ConferenceParticipant } from "@services/meetings/types";
import Button from "@common/components/Button";
import { AddFilter } from "@assets/icons";
import FiltersDrawer from "./FiltersDrawer";
import { isParticipantConnected } from "@common/utils/meetings";
import { participantSortFn } from "../../MonitoringTab/components/const";
import DeviceCard from "../../MonitoringTab/components/Card/DeviceCard";
import { Backspace } from "@mui/icons-material";
import { Frame } from "@pages/Assets/Room/DetailView/CommonTab/DetailFrame/components";

export const participantsFilters = [
  "Invited and not Connected",
  "Only Connected",
  "Only Rooms",
  "Only Participants",
];

type DetailFrameProps = {
  element: React.RefObject<HTMLDivElement | null>;
};
export type MonitorFrameType =
  | {
      index: number;
      participant: ConferenceParticipant;
    }
  | undefined;

const DetailFrame = (props: DetailFrameProps) => {
  const { element } = props;
  const { t } = useTranslation();
  const context = React.useContext(RoomsRealtimeContext);
  const { meeting, loading } = { ...context.detail };

  const [search, setSearch] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [participantFilter, setParticipantFilter] = useState<string>("");
  const [secondaryCodecs, setSecondaryCodecs] = useState<
    ConferenceParticipant[]
  >([]);
  const ref = useRef<HTMLDivElement>(null);
  const [monitor, setMonitor] = useState<MonitorFrameType>();

  const applyFilters = (participant: ConferenceParticipant): boolean => {
    let inSearch = false;
    if (participant.room === undefined) {
      inSearch =
        participant.name?.toLowerCase().includes(search.toLowerCase()) ||
        participant.address?.toLowerCase().includes(search.toLowerCase())
          ? true
          : false;
    } else {
      inSearch =
        participant.room.name?.toLowerCase().includes(search.toLowerCase()) ||
        participant.device?.name
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        participant.room?.location?.name
          ?.toLowerCase()
          .includes(search.toLowerCase())
          ? true
          : false;
    }

    if (inSearch) {
      switch (participantFilter) {
        case "Invited and not Connected":
          if (
            !isParticipantConnected(participant) &&
            (participant.scheduled || participant.released)
          )
            return true;
          break;
        case "Only Connected":
          if (isParticipantConnected(participant)) return true;
          break;
        case "Only Rooms":
          if (participant.room) return true;
          break;
        case "Only Participants":
          if (participant.room === undefined) return true;
          break;
        case "":
          return true;
        default:
          break;
      }
    }
    return false;
  };

  const isPrimaryCodecs = (
    participant: ConferenceParticipant,
    participantRoom: ConferenceParticipant[]
  ) =>
    participantRoom.every(
      (part) =>
        part.id === participant.id ||
        (part.device?.details?.position &&
          participant?.device?.details?.position &&
          part.device?.details?.position <
            participant?.device?.details?.position)
    );
  const disableSecondaryCodecs = (participant?: ConferenceParticipant) => {
    if (!participant) {
      setSecondaryCodecs([]);
      return;
    }
    const participantRoom: ConferenceParticipant[] =
      meeting?.participants
        ?.filter((participant) => applyFilters(participant))
        .filter((part) => part.room?.id === participant.room?.id) ?? [];
    if (!isPrimaryCodecs(participant, participantRoom)) {
      return;
    }

    const secondaryCodecs = participantRoom.filter((part) => {
      if (
        part.id !== participant.id &&
        part.device?.details?.position &&
        participant?.device?.details?.position &&
        part.device?.details?.position > participant?.device?.details?.position
      ) {
        return part;
      }
      return;
    });
    return setSecondaryCodecs(secondaryCodecs);
  };

  const displayCards = () => {
    const participants = meeting?.participants
      ?.filter((participant) => applyFilters(participant))
      .sort(participantSortFn)
      .map((participant, index) => (
        <ParticipantCard
          key={participant.id}
          index={index}
          monitor={monitor}
          setMonitor={setMonitor}
          participant={participant}
          previousPage={meeting.meetingId?.toString() ?? ""}
          handleClickInvite={disableSecondaryCodecs}
          secondaryCodecs={secondaryCodecs}
        />
      ));
    if (monitor && participants) {
      const cardsPerLine = Math.floor((ref.current?.clientWidth ?? 0) / 225);
      const index =
        cardsPerLine - (monitor.index % cardsPerLine) + monitor.index;
      return [
        ...participants.slice(0, index),
        <DeviceCard
          participant={monitor.participant}
          hideHeader={true}
          hideFooter={true}
        />,
        ...participants.slice(index),
      ];
    }
    return participants;
  };

  return (
    <Frame ref={element}>
      <FiltersDrawer
        open={open}
        onClose={setOpen}
        setParticipantFilter={setParticipantFilter}
        participantFilter={participantFilter}
      />
      <WithGrow
        direction="column"
        fixed={
          <Grid
            sx={{
              height: "100%",
            }}
          >
            <WithGrow
              direction="row"
              height="100%"
              sx={{
                alignItems: "center",
                py: 1,
                "& > *": { height: "100%" },
              }}
              fixed={
                <Grid
                  container
                  direction="row"
                  sx={{
                    width: "100%",
                    alignItems: "center",
                    gap: "1rem",
                    height: "100%",
                  }}
                >
                  {meeting && (
                    <>
                      <Typography>
                        {t("{{count}} provisioned rooms", {
                          count: meeting?.participants?.reduce(
                            (total, participant) =>
                              participant.room ? total + 1 : total,
                            0
                          ),
                        })}
                      </Typography>
                      <Divider orientation="vertical" />
                      <Typography>
                        {t("{{ COUNT }} provisioned devices", {
                          COUNT: meeting?.participants?.reduce(
                            (total, participant) =>
                              participant.device ? total + 1 : total,
                            0
                          ),
                        })}
                      </Typography>
                      <TextField
                        label={t("Search")}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  color="inherit"
                                  onClick={() => setSearch("")}
                                >
                                  <Backspace />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }
                        }}
                      />
                    </>
                  )}
                </Grid>
              }
            >
              <WithGrow
                direction="row-reverse"
                fixed={
                  <Grid
                    container
                    direction="row"
                    sx={{ width: "100%", height: "100%", alignItems: "center" }}
                  >
                    <Button
                      onClick={() => setOpen(true)}
                      startIcon={<AddFilter />}
                      variant="outlined"
                      ignoreLocalization
                    >
                      {t("{{ COUNTER }} more filters", {
                        COUNTER: participantFilter === "" ? 0 : 1,
                      })}
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => {
                        setSearch("");
                        setParticipantFilter("");
                      }}
                      color="greyed"
                    >
                      Clear filters
                    </Button>
                  </Grid>
                }
              ></WithGrow>
            </WithGrow>
            <Divider orientation="horizontal" />
          </Grid>
        }
      >
        <Grid
          container
          sx={{
            height: "100%",
            overflow: "auto",
            gap: 2,
            padding: (theme) => theme.spacing(2, 0),
          }}
        >
          {loading ? (
            <CircularProgress
              sx={{ position: "relative", top: "50%", left: "50%" }}
            />
          ) : (
            <Grid
              sx={{
                width: "100%",
              }}
            >
              <Grid ref={ref} container sx={{ width: "100%", gap: "1rem" }}>
                {displayCards()}
              </Grid>
            </Grid>
          )}
        </Grid>
      </WithGrow>
    </Frame>
  );
};

export default DetailFrame;
